import React, {useState, useEffect, useRef} from "react";
import {Container, Card} from "react-bootstrap";
import axios from "../../util/Axios/AxiosInstance";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, {textFilter} from "react-bootstrap-table2-filter";
import "react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css";
import eyeImage from "../../../images/eye.png";
import TableActionIcon from "../../util/TableActionIcon";
import AOS from "aos";
import "aos/dist/aos.css";
import Skeleton from "react-loading-skeleton";
import {format} from "date-fns";
import "../ReportsSection/ReportSection.css";
import {PAYOUT_SOURCE_FACIAL} from "./PayoutSourceToggle";
import PayoutToolbar from "./PayoutToolbar";
import {exportPayoutTableToCsv} from "./payoutExportUtils";

const createSkeletonRows = () =>
    Array.from({length: 6}, () => ({
        branch: <Skeleton/>,
        contractName: <Skeleton/>,
        vendorName: <Skeleton/>,
        totalMandays: <Skeleton/>,
        totalAmountPayable: <Skeleton/>,
    }));

const formatAmount = (value) => {
    if (value == null || value === "") {
        return "";
    }
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : "";
};

function Payouts({HandleEventGetVendorId, payoutSource: initialSource}) {
    const dateToday = new Date();
    const firstDay = new Date(dateToday.getFullYear(), dateToday.getMonth(), 1);
    const lastDay = new Date(dateToday.getFullYear(), dateToday.getMonth() + 1, 0);

    const [tableData, setTableData] = useState(createSkeletonRows());
    const [loading, setLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [open, setOpen] = useState(false);
    const [payoutSource, setPayoutSource] = useState(initialSource || PAYOUT_SOURCE_FACIAL);
    const pickerRef = useRef(null);
    const [range, setRange] = useState([
        {
            startDate: firstDay,
            endDate: lastDay,
            key: "selection",
        },
    ]);

    const options = {
        sizePerPage: pageSize,
        onSizePerPageChange: (sizePerPage) => {
            setPageSize(sizePerPage);
        },
        onPageChange: (page) => {
            setPageNumber(page);
        },
    };

    const getVendorPayoutSummary = async (selectedRange, source) => {
        const branchId = localStorage.getItem("branchId");
        const startDate = format(selectedRange[0].startDate, "yyyy-MM-dd");
        const endDate = format(selectedRange[0].endDate, "yyyy-MM-dd");
        setLoading(true);
        setTableData(createSkeletonRows());
        try {
            const response = await axios.get(
                `payout/vendors/${branchId}/${startDate}/${endDate}`,
                {params: {source: source || payoutSource}}
            );
            setTableData(Array.isArray(response.data) ? response.data : []);
        } catch (e) {
            console.error(e);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        AOS.init();
    }, []);

    useEffect(() => {
        if (open) {
            return;
        }
        getVendorPayoutSummary(range, payoutSource);
    }, [open, range, payoutSource]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const columns = [
        {
            dataField: "vendorId",
            text: "S No.",
            formatter: (cell, row, rowIndex) => (pageNumber - 1) * pageSize + (rowIndex + 1),
            headerStyle: () => ({width: "60px", textAlign: "center"}),
        },
        {
            dataField: "branch",
            text: "Branch",
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "contractName",
            text: "Contract Name",
            sort: true,
            filter: textFilter(),
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "vendorName",
            text: "Vendor Name",
            sort: true,
            filter: textFilter(),
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "totalMandays",
            text: "Total Mandays",
            sort: true,
            formatter: (cell) => formatAmount(cell),
            title: (cell) => `${cell ?? ""}`,
        },
        {
            dataField: "totalAmountPayable",
            text: "Total Amount Payable",
            sort: true,
            formatter: (cell, row) =>
                row?.wagesAvailable ? formatAmount(cell) : "",
            title: (cell, row) =>
                row?.wagesAvailable ? `${cell ?? ""}` : "Wage master not available for this period",
        },
                        {
            dataField: "actions",
            text: "View",
            isDummyField: true,
            headerStyle: () => ({width: "70px", textAlign: "center"}),
            formatter: (cellContent, row) => (
                <TableActionIcon
                    src={eyeImage}
                    title="View labour-wise payout"
                    size={28}
                    disabled={!row?.vendorId}
                    onClick={() => {
                        if (row?.vendorId) {
                            HandleEventGetVendorId(102, row.vendorId, range, payoutSource);
                        }
                    }}
                />
            ),
        },
    ];

    const exportDateLabel = `${format(range[0].startDate, "dd-MM-yyyy")}_${format(
        range[0].endDate,
        "dd-MM-yyyy"
    )}`;

    const handleExport = () => {
        exportPayoutTableToCsv({
            columns,
            data: tableData,
            fileName: "VendorPayout",
            dateLabel: exportDateLabel,
        });
    };

    return (
        <Card className="cardStyl">
            <Container fluid className="px-0">
                <div className="mt-2 d-flex align-items-center gap-2">
                    <h5 className="priColor mb-0">Payouts</h5>
                    {loading && (
                        <span className="text-muted" style={{fontSize: "0.85rem"}}>Loading...</span>
                    )}
                </div>
                <PayoutToolbar
                    source={payoutSource}
                    disabled={loading}
                    onSourceChange={(next) => {
                        setPageNumber(1);
                        setPayoutSource(next);
                    }}
                    range={range}
                    setRange={setRange}
                    open={open}
                    setOpen={setOpen}
                    pickerRef={pickerRef}
                    onExport={handleExport}
                    exportDisabled={loading || !tableData.length}
                />
                <div data-aos="zoom-in-right" data-aos-duration="550">
                    <BootstrapTable
                        bootstrap4
                        keyField="vendorId"
                        data={tableData}
                        columns={columns}
                        wrapperClasses="table-responsive"
                        pagination={paginationFactory(options)}
                        filter={filterFactory()}
                        noDataIndication={loading ? "Loading..." : "No payout data available"}
                    />
                </div>
            </Container>
        </Card>
    );
}

export default Payouts;
